import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Edit } from "lucide-react";
import Layout from "./layout";
import { userManagementData } from "../data/userManagementMockData";
import UserManagementModal from "../Modal/UserManagementModal";
import EditUserManagementModal from "../Modal/EditUserManagementModal";
import { consultantAPI } from "../services/api";

const PageContainer = styled.div`
  font-family: "Prompt", sans-serif;
  background-color: #ffffff;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #686868;
  min-width: 120px;
`;

const FilterInput = styled.input`
  padding: 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 12px;
  font-family: "Prompt", sans-serif;
  width: 160px;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: inline-flex;
  gap: 1rem;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 12px;
  font-weight: 400;
  color: #686868;
  cursor: pointer;
  white-space: nowrap;
  min-width: fit-content;
`;

const Checkbox = styled.input`
  width: 0.875rem;
  height: 0.875rem;
  accent-color: #00b5b8;
  cursor: pointer;
`;

const SearchLabel = styled.label`
  font-size: 12px;
  font-weight: 400;
  color: #686868;
  white-space: nowrap;
`;

const SearchInput = styled.input`
  padding: 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 12px;
  font-family: "Prompt", sans-serif;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #000000;
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  padding: 0.4rem 1.5rem;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  font-family: "Prompt", sans-serif;

  &:hover {
    background-color: #1f2937;
  }
`;

const CreateButton = styled.button`
  background-color: #000000;
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: "Prompt", sans-serif;
  white-space: nowrap;

  &:hover {
    background-color: #1f2937;
  }
`;

const TableWrapper = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem 0.3rem;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #a3a3a3;
`;

const TableCell = styled.td`
  padding: 0.5rem 0.3rem;
  font-size: 12px;
  text-align: center;
  font-weight: 400;
  color: #686868;
`;

const CampaignBadge = styled.span`
  background-color: #1d87f0;
  color: #ffffff;
  padding: 0.2rem 0.5rem;
  border-radius: 0px;
  font-size: 11px;
  font-weight: 400;
  display: inline-block;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 0.25rem;
  color: #1d87f0;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.4rem 0.6rem;
  border: none;
  background-color: ${(props) => (props.active ? "#1976D2" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#1976D2")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-size: 0.75rem;
  font-family: "Prompt", sans-serif;
  min-width: 35px;
  text-decoration: ${(props) => (props.active ? "none" : "underline")};

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const UserManagementPage = () => {
  const [filters, setFilters] = useState({
    หมอ: false,
    พยาบาล: false,
    เจ้าหน้าที่อื่น: false,
    manager: false,
    analyze: false,
  });

  const [searchName, setSearchName] = useState("");
  const [searchField, setSearchField] = useState("");
  const [searchCompany, setSearchCompany] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeFilters, setActiveFilters] = useState({});
  const typeConsultKeywordMap = [
    "หมอ",
    "พยาบาล",
    "เจ้าหน้าที่อื่น",
    "manager",
    "analyze",
  ];

  const buildTypeConsultKeywords = (sourceFilters = filters) =>
    Object.values(sourceFilters)
      .map((isSelected, index) =>
        isSelected ? typeConsultKeywordMap[index] : null
      )
      .filter(Boolean);

  useEffect(() => {
    fetchConsultants(currentPage, activeFilters);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const fetchConsultants = async (page = 1, extraFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await consultantAPI.getList({ pageNow: page, ...extraFilters });

      console.log("🔍 fetchConsultants - Raw API Response:", response);

      if (response.list && Array.isArray(response.list)) {
        const mappedConsultants = response.list.map((consultant) => {
          const nameParts = [
            consultant.nameprefix,
            consultant.name,
            consultant.lastname,
          ].filter((part) => part && part.trim() !== "");

          const fullName = nameParts.join(" ").trim() || "-";

          const roles = consultant.role || [];
          const isAdminDoctor = Array.isArray(roles)
            ? roles.includes("admin_doctor")
            : consultant.isAdminDoctor || false;

          return {
            ...consultant,
            id: consultant.id,
            code: consultant.id,
            username: consultant.username || "-",
            displayName: fullName,
            isSuperAdmin: consultant.level === "1",
            isAdminDoctor: isAdminDoctor,
            type: consultant.typeconsult || consultant.position || "หมอ",
            field: consultant.typebranch || consultant.job || "-",
            company: "-",
            campaign:
              consultant.consultCampaign &&
              consultant.consultCampaign.length > 0
                ? consultant.consultCampaign[0].name
                : "-",
          };
        });
        console.log("✅ Mapped consultants:", mappedConsultants);
        setConsultants(mappedConsultants);
        setTotalPages(parseInt(response.pageAmount) || 1);
        if (page === 1 && mappedConsultants.length > 0) setPageSize(mappedConsultants.length);
      } else if (response.result === false && response.responseText) {
        console.warn("⚠️ API returned no data:", response.responseText);
        setConsultants([]);
      } else if (response.data && Array.isArray(response.data)) {
        setConsultants(response.data);
      } else if (Array.isArray(response)) {
        setConsultants(response);
      } else {
        console.warn("⚠️ API response format unknown:", response);
        setConsultants([]);
      }
    } catch (err) {
      console.error("❌ Error fetching consultants:", err);
      setError(err.message);
      setConsultants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const handleSaveModal = async (data) => {
    try {
      await consultantAPI.add(data);
      fetchConsultants();
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error saving consultant:", err);
      setError("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + err.message);
    }
  };

  const handleUpdateConsultant = async (data) => {
    try {
      const response = await consultantAPI.update(data);

      if (response.result) {
        await fetchConsultants();
        setIsEditModalOpen(false);
        setEditingConsultant(null);
        alert("บันทึกข้อมูลสำเร็จ");
      } else {
        const errorMsg =
          response.responseText || "เกิดข้อผิดพลาดในการอัพเดทข้อมูล";
        setError(errorMsg);
        alert(errorMsg);
      }
    } catch (err) {
      console.error("Error updating consultant:", err);
      setError("เกิดข้อผิดพลาดในการอัพเดทข้อมูล: " + err.message);
      alert("เกิดข้อผิดพลาดในการอัพเดทข้อมูล: " + err.message);
    }
  };

  const handleEditConsultant = (consultant) => {
    console.log(
      "🔍 UserManagement - Opening edit modal for consultant:",
      consultant,
    );
    setEditingConsultant(consultant);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingConsultant(null);
  };

  const handleSearch = async () => {
    const searchFilters = {};

    if (searchName && searchName.trim()) {
      const keywords = searchName.trim().split(/\s+/);
      searchFilters.keywordName = keywords[0];
    }

    const selectedTypeConsults = buildTypeConsultKeywords(filters);
    if (selectedTypeConsults.length > 0) {
      searchFilters.keywordTypeConsult = selectedTypeConsults;
    }

    setActiveFilters(searchFilters);

    // reset หน้า 1 — ถ้า currentPage ยังเป็น 1 ให้ fetch ตรงๆ เพราะ useEffect ไม่ trigger ซ้ำ
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    setLoading(true);
    setCurrentPage(1);
    try {
      const response = await consultantAPI.getList({ pageNow: 1, ...searchFilters });
      const selectedTypes = Object.keys(filters).filter((key) => filters[key]);

      console.log("🔍 handleSearch - Raw API Response:", response);
      console.log("🔍 Selected filters:", selectedTypes);

      if (response.list && Array.isArray(response.list)) {
        let mappedConsultants = response.list.map((consultant) => {
          const nameParts = [
            consultant.nameprefix,
            consultant.name,
            consultant.lastname,
          ].filter((part) => part && part.trim() !== "");

          const fullName = nameParts.join(" ").trim() || "-";

          const roles = consultant.role || [];
          const isAdminDoctor = Array.isArray(roles)
            ? roles.includes("admin_doctor")
            : consultant.isAdminDoctor || false;

          return {
            ...consultant,
            id: consultant.id,
            code: consultant.id,
            username: consultant.username || "-",
            displayName: fullName,
            isSuperAdmin: consultant.level === "1",
            isAdminDoctor: isAdminDoctor,
            type: consultant.typeconsult || consultant.position || "หมอ",
            field: consultant.typebranch || consultant.job || "-",
            company: "-",
            campaign:
              consultant.consultCampaign &&
              consultant.consultCampaign.length > 0
                ? consultant.consultCampaign[0].name
                : "-",
          };
        });
        if (searchName && searchName.trim()) {
          const keywordInput = searchName.toLowerCase().trim();
          const keywords = keywordInput.split(/\s+/).filter(k => k.length > 0);

          if (keywords.length > 0) {
            mappedConsultants = mappedConsultants.filter((consultant) => {
              const displayName = (consultant.displayName || '').toLowerCase();
              const username = (consultant.username || '').toLowerCase();
              const searchableText = `${displayName} ${username}`;
              return keywords.every(kw => searchableText.includes(kw));
            });
          }
        }

        if (selectedTypes.length > 0) {
          mappedConsultants = mappedConsultants.filter((consultant) => {
            const consultantRoles = consultant.role || [];
            return selectedTypes.some((selectedType) => {
              if (selectedType === "หมอ") {
                return (
                  consultant.type === "หมอ" || consultant.type === "doctor"
                );
              } else if (selectedType === "พยาบาล") {
                return (
                  consultant.type === "พยาบาล" || consultant.type === "nurse"
                );
              } else if (selectedType === "manager") {
                return (
                  consultant.type === "manager" ||
                  (Array.isArray(consultantRoles) &&
                    consultantRoles.includes("manager"))
                );
              } else if (selectedType === "analyze") {
                return (
                  consultant.type === "analyze" ||
                  (Array.isArray(consultantRoles) &&
                    consultantRoles.includes("analyze"))
                );
              } else if (selectedType === "เจ้าหน้าที่อื่น") {
                return (
                  consultant.type === "เจ้าหน้าที่อื่น" ||
                  consultant.type === "other"
                );
              }
              return consultant.type === selectedType;
            });
          });
        }

        console.log("✅ Search results (after filtering):", mappedConsultants);
        setConsultants(mappedConsultants);
        setTotalPages(parseInt(response.pageAmount) || 1);
        setPageSize(mappedConsultants.length > 0 ? mappedConsultants.length : pageSize);
      } else if (response.result === false && response.responseText) {
        console.warn("⚠️ API returned no data:", response.responseText);
        setConsultants([]);
        setTotalPages(1);
      } else if (response.data && Array.isArray(response.data)) {
        setConsultants(response.data);
      } else if (Array.isArray(response)) {
        setConsultants(response);
      } else {
        console.warn("⚠️ API response format unknown:", response);
        setConsultants([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("❌ Error searching consultants:", err);
      setError(err.message);
      setConsultants([]);
    } finally {
      setLoading(false);
    }
  };

  const currentData = consultants;

  return (
    <Layout>
      <PageContainer>
        {loading && (
          <div
            style={{ textAlign: "center", padding: "1rem", fontSize: "12px" }}
          >
            กำลังโหลดข้อมูล...
          </div>
        )}
        {error && (
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "#EF4444",
              fontSize: "12px",
            }}
          >
            เกิดข้อผิดพลาด: {error}
          </div>
        )}

        <HeaderSection>
          <ButtonRow>
            <CreateButton onClick={() => setIsModalOpen(true)}>
              เพิ่มที่ปรึกษา
            </CreateButton>
          </ButtonRow>

          <FilterRow>
            <FilterGroup>
              <FilterLabel>ค้นหาจาก ชื่อ-นามสกุล</FilterLabel>
              <FilterInput
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <FilterLabel style={{ marginLeft: "1rem" }}>ประเภท</FilterLabel>
              <CheckboxGroup>
                {Object.keys(filters).map((filter) => (
                  <CheckboxLabel key={filter}>
                    <Checkbox
                      type="checkbox"
                      checked={filters[filter]}
                      onChange={() => handleFilterChange(filter)}
                    />
                    {filter}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FilterGroup>
            <SearchButton onClick={handleSearch}>ค้นหา</SearchButton>
          </FilterRow>
        </HeaderSection>

        <PaginationContainer>
          <PaginationButton
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            previous
          </PaginationButton>
          {[...Array(totalPages)].map((_, index) => (
            <PaginationButton
              key={index + 1}
              active={currentPage === index + 1}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
          >
            next
          </PaginationButton>
        </PaginationContainer>

        <TableWrapper>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>ลำดับ</TableHeaderCell>
                <TableHeaderCell>รหัสที่ปรึกษา</TableHeaderCell>
                <TableHeaderCell>ชื่อ-สกุล</TableHeaderCell>
                <TableHeaderCell>admin doctor</TableHeaderCell>
                <TableHeaderCell>super admin</TableHeaderCell>
                <TableHeaderCell>พยาบาล</TableHeaderCell>
                <TableHeaderCell>manager</TableHeaderCell>
                <TableHeaderCell>analyze</TableHeaderCell>
                <TableHeaderCell>ประเภท</TableHeaderCell>
                <TableHeaderCell>แก้ไข</TableHeaderCell>
              </tr>
            </TableHeader>
            <tbody>
              {currentData.map((user, index) => {
                const roles = user.role || [];
                const isNurse = roles.includes("nurse");
                const isManager = roles.includes("manager");
                const isAnalyze = roles.includes("analyze");

                return (
                  <TableRow key={user.id}>
                    <TableCell>{(currentPage - 1) * pageSize + index + 1}</TableCell>
                    <TableCell>{user.code}</TableCell>
                    <TableCell>{user.displayName}</TableCell>
                    <TableCell>
                      <Checkbox
                        type="checkbox"
                        checked={user.isAdminDoctor}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        type="checkbox"
                        checked={user.isSuperAdmin}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox type="checkbox" checked={isNurse} disabled />
                    </TableCell>
                    <TableCell>
                      <Checkbox type="checkbox" checked={isManager} disabled />
                    </TableCell>
                    <TableCell>
                      <Checkbox type="checkbox" checked={isAnalyze} disabled />
                    </TableCell>
                    <TableCell>{user.type}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditConsultant(user)}>
                        <Edit size={14} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </PageContainer>

      <UserManagementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveModal}
      />

      <EditUserManagementModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleUpdateConsultant}
        consultantData={editingConsultant}
      />
    </Layout>
  );
};

export default UserManagementPage;
